import React, { Component, ErrorInfo, ReactNode, useEffect } from 'react';
import * as Sentry from '@sentry/react';

const RELOAD_COUNT_KEY = 'chunkLoadErrorBoundary.reloadCount';
const MAX_RELOAD_ATTEMPTS = 3;

interface ChunkLoadErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
}

interface ChunkLoadErrorBoundaryState {
  hasError: boolean;
  maxRetriesExceeded: boolean;
  error?: Error;
}

function isChunkLoadError(error: Error): boolean {
  return (
    error.message.includes('Loading chunk') ||
    error.message.includes('ChunkLoadError') ||
    error.message.includes('Failed to fetch dynamically imported module')
  );
}

function getReloadCount(): number {
  try {
    return Number(sessionStorage.getItem(RELOAD_COUNT_KEY) || '0');
  } catch (error) {
    // Log for debugging - storage failures are important to track
    console.warn('[ChunkLoadErrorBoundary] Failed to read reload count:', {
      error,
      errorMessage: error instanceof Error ? error.message : String(error),
    });

    // Add Sentry breadcrumb for visibility
    Sentry.addBreadcrumb({
      category: 'chunk-load-error',
      message: 'Failed to read reload count from sessionStorage',
      level: 'warning',
      data: {
        error: error instanceof Error ? error.message : String(error),
      },
    });

    return 0;
  }
}

function setReloadCount(count: number): void {
  try {
    sessionStorage.setItem(RELOAD_COUNT_KEY, String(count));
  } catch (error) {
    // Log for debugging - this could cause infinite reload loop
    console.error('[ChunkLoadErrorBoundary] Failed to write reload count:', {
      error,
      count,
      errorMessage: error instanceof Error ? error.message : String(error),
    });

    // Report to Sentry as this affects user experience
    Sentry.captureMessage('Failed to track chunk load retry count', {
      level: 'warning',
      extra: {
        error: error instanceof Error ? error.message : String(error),
        attemptedCount: count,
      },
    });
  }
}

function clearReloadCount(): void {
  try {
    sessionStorage.removeItem(RELOAD_COUNT_KEY);
  } catch (error) {
    // Log for debugging - storage failures are important to track
    console.warn('[ChunkLoadErrorBoundary] Failed to clear reload count:', {
      error,
      errorMessage: error instanceof Error ? error.message : String(error),
    });

    // Add Sentry breadcrumb for visibility
    Sentry.addBreadcrumb({
      category: 'chunk-load-error',
      message: 'Failed to clear reload count from sessionStorage',
      level: 'warning',
      data: {
        error: error instanceof Error ? error.message : String(error),
      },
    });
  }
}

function recordReloadAttempt(): boolean {
  const count = getReloadCount();
  if (count >= MAX_RELOAD_ATTEMPTS) {
    return false;
  }

  setReloadCount(count + 1);
  return true;
}

/**
 * Mount inside a Suspense boundary (as a sibling of the lazy route).
 * Only mounts after the lazy chunk has resolved, so clearing the retry
 * budget cannot race a still-pending import.
 */
export function ChunkLoadSettleSignal(): null {
  useEffect(() => {
    clearReloadCount();
  }, []);

  return null;
}

export default class ChunkLoadErrorBoundary extends Component<
  ChunkLoadErrorBoundaryProps,
  ChunkLoadErrorBoundaryState
> {
  constructor(props: ChunkLoadErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, maxRetriesExceeded: false };
  }

  static getDerivedStateFromError(
    error: Error,
  ): Partial<ChunkLoadErrorBoundaryState> {
    if (isChunkLoadError(error)) {
      return { hasError: true, error };
    }

    // For non-chunk errors, return hasError: false so we don't render our
    // fallback UI. The error will be re-thrown in componentDidCatch to
    // propagate to a parent error boundary.
    return { hasError: false };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    if (!isChunkLoadError(error)) {
      // Re-throw non-chunk errors so they propagate to parent error boundaries
      // instead of being silently swallowed by this boundary.
      throw error;
    }

    console.error('Chunk load error caught:', error, errorInfo);

    if (!recordReloadAttempt()) {
      // Stop the reload loop — persistent CDN failure needs manual intervention
      this.setState({ maxRetriesExceeded: true });
      return;
    }

    setTimeout(() => {
      window.location.reload();
    }, 2000);
  }

  render() {
    if (this.state.maxRetriesExceeded) {
      return (
        this.props.fallback || (
          <div className="flex h-screen items-center justify-center">
            <div className="text-center">
              <h2 className="text-xl mb-2 font-semibold">
                Something went wrong
              </h2>
              <p className="text-neutral-600">
                We're having trouble loading the application. Please refresh the
                page or try again later.
              </p>
            </div>
          </div>
        )
      );
    }

    if (this.state.hasError) {
      return (
        <div className="flex h-screen items-center justify-center">
          <div className="text-center">
            <h2 className="text-xl mb-2 font-semibold">Loading...</h2>
            <p className="text-neutral-600">
              Please wait while we reload the page
            </p>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
